/**
 * Date helpers for Dagboek (spec §8.7).
 *
 * All journal dates are local wall-clock dates in ISO yyyy-mm-dd form
 * (see src/types JournalEntry.date). Formatting is Dutch (nl-NL).
 */

/** Local date as ISO yyyy-mm-dd (no UTC shifting). */
export function toLocalISODate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Today as local ISO yyyy-mm-dd. */
export function todayISODate(): string {
  return toLocalISODate(new Date())
}

/** Parses a local ISO yyyy-mm-dd string. Returns null when invalid. */
export function parseISODate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return date
}

/** Shifts a local ISO date by a number of days. Returns null on invalid input. */
export function addDaysISO(iso: string, days: number): string | null {
  const date = parseISODate(iso)
  if (!date) return null
  date.setDate(date.getDate() + days)
  return toLocalISODate(date)
}

export function isValidISODate(iso: string): boolean {
  return parseISODate(iso) !== null
}

export function isTodayISO(iso: string): boolean {
  return iso === todayISODate()
}

/** True when the ISO date is after today (local). */
export function isFutureISO(iso: string): boolean {
  return iso > todayISODate()
}

const longFormatter = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const shortFormatter = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

/** e.g. 'maandag 3 augustus 2026'. Falls back to the raw ISO when invalid. */
export function formatDateLongNL(iso: string): string {
  const date = parseISODate(iso)
  return date ? longFormatter.format(date) : iso
}

/** e.g. 'ma 3 aug 2026'. Falls back to the raw ISO when invalid. */
export function formatDateShortNL(iso: string): string {
  const date = parseISODate(iso)
  return date ? shortFormatter.format(date) : iso
}
